import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { employeeModel, FormValues } from '~/entities/common/Taxipark/Employee'
import { CarRental, Region, Role } from '~/shared/api'
import {
  localeEnumOptions,
  timezoneCodeOptions,
} from '~/shared/config/constants'
import { getPhoneMaskByCountry } from '~/shared/config/countryConfig'
import { LocaleEnum } from '~/shared/config/enums'
import { featureFlagModel } from '~/shared/lib/featureFlag'
import { getObject } from '~/shared/lib/utils'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  MaskInput,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

export type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()
  const featureFlag = useStore(featureFlagModel.$featureFlags)

  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(employeeModel.formSchema),
    defaultValues,
  })

  const selectedLocale = form.watch('locale')
  const selectedLocaleId = getObject(selectedLocale)?.id

  const filteredLocaleEnumOptions = useMemo(() => {
    return localeEnumOptions.filter((option) => {
      if (option.id === LocaleEnum.fr) {
        return !!featureFlag.visible_locale_france
      } else {
        return true
      }
    })
  }, [featureFlag.visible_locale_france])

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='form.full name'
              placeholder='form.enter full name'
              name='name'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <MaskInput
              name='phone'
              label='form.phone'
              placeholder='form.enter phone'
              skeletonShow={isLoading}
              readOnly={disabled}
              mask={getPhoneMaskByCountry()}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput
              label='form.E-mail'
              placeholder='form.enter E-mail'
              name='email'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput
              label='form.telegram id'
              placeholder='form.enter telegram id'
              name='telegramUserId'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-regions'>
        <ContainerTitle>{t('form.regions')}</ContainerTitle>
        <Grid container spacing={2.5} alignItems='flex-end'>
          <Grid container xs={6}>
            <AsyncAutocompleteInput
              name='regionsIds'
              placeholder='form.add region'
              fetchOptions={Region.fetchOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              multiple
              autocompleteBoxSx={{ padding: 0 }}
              autocompleteXs={12}
              dataTestId='Регионы'
            />
          </Grid>
          <Grid xs={3}>
            <AutocompleteInput
              name='timezone'
              label='form.time zone'
              placeholder='form.time zone'
              options={timezoneCodeOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={3}>
            <AutocompleteInput
              name='locale'
              label='form.localization'
              placeholder='form.select'
              options={filteredLocaleEnumOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              startDecorator={
                selectedLocaleId ? (
                  <img
                    src={`/icons/${selectedLocaleId}.png`}
                    width='26px'
                    alt='icon flag'
                  />
                ) : null
              }
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-roles'>
        <ContainerTitle>{t('roles')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='rolesIds'
            placeholder='form.add role'
            fetchOptions={Role.fetchOptions}
            skeletonShow={isLoading}
            readOnly={disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Роли'
          />
        </Grid>
      </Container>

      <Container data-testid='container-car-rentals'>
        <ContainerTitle>{t('lessors')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='carRentalsIds'
            placeholder='form.select'
            fetchOptions={CarRental.fetchOptions}
            skeletonShow={isLoading}
            readOnly={disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Арендодатели'
          />
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
